<template>
    <v-dialog v-model="show" width="auto" persistent>
        <v-card width="800" title="Add External Estimate">
            <v-container>
                <v-form ref="externalEstimateForm" lazy-validation>
                    <v-row>
                        <v-col>
                            <v-text-field v-model="dialogCore.name" label="Name" placeholder="Name" validate-on="blur"
                                @blur="convertEmptyToNull('name')" hint="Minimum of 3 characters required"
                                persistent-hint :rules="[
                                    rules.required,
                                    rules.min(3),
                                    rules.max(64)
                                ]">
                            </v-text-field>
                        </v-col>
                        <v-col>
                            <v-select v-model="dialogCore.default_adjustment_factor" :items="adjustmentFactors"
                                item-title="type" item-value="type" label="Default Adjustment Factor*"
                                placeholder="Default Adjustment Factor"
                                @blur="convertEmptyToNull('default_adjustment_factor')" :rules="[
                                    rules.required,
                                ]">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props"
                                        :disabled="defaultDisableList(item.value)"></v-list-item>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>


                    <v-row>
                        <v-col>
                            <v-file-input
                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                prepend-icon="mdi-microsoft-excel" label="File input" @change="onFileUpload"
                                v-model="uploadedFile" :error-messages="fileInputError" :rules="[
                                    rules.requiredFileUpload
                                ]"></v-file-input>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="d-flex ga-4 justify-end">
                            <v-btn color="primary" variant="outlined" @click="dialogCancel()">
                                Cancel
                            </v-btn>
                            <v-btn color="primary" variant="flat" :loading="checkingSpreadsheetFormat"
                                @click="dialogSubmit()">
                                Add
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-container>
        </v-card>
    </v-dialog>
</template>
<script setup>
import { ref, defineEmits, defineProps, toRefs } from 'vue';
import { useFieldValidation } from "@/assets/modules/composables.js";
import rules from "@/assets/modules/rules.js";
// eslint-disable-next-line no-unused-vars
import ExcelJS from "exceljs";
const props = defineProps(['currentExternalEstimates', 'show', 'adjustmentFactors']);
const { show } = toRefs(props);
const emit = defineEmits(['externalEstimateAdded', 'closeDialog']);
// const { entityAncestry } = useEntityAncestry();
import { useStore } from 'vuex';

const externalEstimateForm = ref();
const checkingSpreadsheetFormat = ref(false);
const fileInputError = ref("");
const dialogCore = ref({});
const { convertEmptyToNull } = useFieldValidation(dialogCore);
const store = useStore();
// const isLoading = reactive({});
// const alreadyFetchedData = [];
// const { fetchList } = useFetchList(isLoading, alreadyFetchedData);
const uploadedFile = ref(null);
function defaultDisableList(type) {
    const npp = dialogCore.value.npp_adjustment_factor;
    const isDisabled = npp === type && npp !== undefined;
    return isDisabled;
}
// function nppDisableList(type) {
//     const npp = dialogCore.value.default_adjustment_factor;
//     const isDisabled = npp === type && npp !== undefined;
//     return isDisabled;
// }

function dialogCancel() {
    emit('closeDialog');
    dialogCore.value = {};
    uploadedFile.value = null;
}
async function dialogSubmit() {
    const formValidation = await externalEstimateForm.value.validate();
    if (!formValidation.valid) {
        throw new Error("Form validation failed");
    }
    checkingSpreadsheetFormat.value = true;
    // checkProposalFormat
    const isProposalFormatValid = await store.dispatch("checkProposalFormat", {
        payload: dialogCore.value,
    });
    console.log('isProposalFormatValid', isProposalFormatValid)
    if (isProposalFormatValid) {
        emit('externalEstimateAdded', dialogCore.value);
        emit('closeDialog');
        dialogCore.value = {};
        uploadedFile.value = null;
    } else {
        fileInputError.value = "Invalid Excel file. Please upload a valid .xlsx file.";
    }
    checkingSpreadsheetFormat.value = false;
}
/* eslint-disable no-unused-vars */
function onFileUpload() {
    if (uploadedFile.value) {
        const reader = new FileReader();
        reader.onloadend = function () {
            const arrayBuffer = reader.result;
            const workbook = new ExcelJS.Workbook();

            workbook.xlsx.load(arrayBuffer).then(function (workbook) {
                //get sheet 1, the data
                const estimates = workbook.worksheets[0];
                console.log("estimates", estimates)
                let items = sheetToJson({
                    sheet: estimates,
                    headerRowNumber: 1,
                });
                dialogCore.value.spreadsheet = items;
            });
        }
        reader.readAsArrayBuffer(uploadedFile.value);
    }
    console.log("dialogCore.value", dialogCore.value)
}

function sheetToJson({ sheet, headerRowNumber = 1 }) {
    //header row starts at 6 in this case
    //the formatting has changed and may change again.
    //headerRowNumber indicates where the data starts to improt

    //content rows get one added
    let contentRowStart = headerRowNumber;


    let headerRow = sheet.getRow(headerRowNumber);
    headerRow = headerRow._cells.map((cell) => {

        if (cell && cell.value) {
            const header = cell.value.replace(/\s/g, "");
            return {
                column: cell._column._number,
                address: cell.address,
                value: header,
            };
        } else {
            return null;
        }
    });
    const headers = [];
    let npp = "";

    headerRow.forEach((cell) => {
        headers[cell.column] = cell.value;

        //we were originally using the column namned NPP, but got 
        // to switched color, which would need an update here.
        //set the header for NPP, gonna use it later
        if (cell.value.toLowerCase() === "npp") {
            npp = cell.column;
        }
    });

    const payload = [];
    //walk rows and build JSON
    //row should skip blank rows and start at 6.
    //the length is the rows property which goes on forever in Excel
    //we watch for a blank line


    for (var i = contentRowStart; i < sheet._rows.length; i++) {
        const item = {}
        const row = sheet._rows[i]._cells;

        //check for a blank row, stop reading rows
        //the first record will always be blank now because of NPP, so let's use the first cell.
        if (!row[1]) {
            break;
        }


        //headers start at column 1, cells start at index 0, REALLY WEIRD
        for (var j = 1; j < headers.length; j++) {
            //j is the cell number in the cell array, work, but DAMN son, ugly.
            let key = headers[j];
            const value = row[(j - 1)]?.value;

            if (key == "UnitPrice") {
                key = "Total"
            }


            // we know that the description field ends with - NPP Item, so that is who we match
            if (key.toLowerCase() == "npp") {
                if (value && (value.toLowerCase() == ("npp") || value.toLowerCase() == "x")) {
                    item["NPP"] = true;
                } else {
                    item["NPP"] = false;
                }
            }

            // set the thing, but kill Line Total, use the preceeding if to not overwrite NPP values
            else if (key != "LineTotal") {
                item[key] = value;
            }

        }


        //get the code, pull the first two digits which are the category and set on the record
        let code = item["Code"];
        if (code) {
            item["Category"] = code.substring(0, 2);
        }

        payload.push(item);
    }
    console.log(payload)
    return payload;
}



















// const ownerDoc = computed(() => {
//     return store.getters.getEntityDocument(entityAncestry.value.owner);
// });

// const subcontractorsList = computed(() => {
//     const items = store.getters.getChildList("owner." + entityAncestry.value.owner + ".", "subcontractor");
//     const hint = ownerDoc.value?._name ? `List of subcontractors from ${ownerDoc.value?._name}.` : 'List of subcontractors.'
//     const disabled = !ownerDoc.value?._name;
//     const loading = isLoading['subcontractor_owner-' + entityAncestry.value.owner];
//     return {
//         items: items,
//         hint: hint,
//         disabled: disabled,
//         loading: loading,
//     };
// })

// function isAlreadyChosen(item) {
//     return props.adjustmentFactors.some(adjustmentFactor => adjustmentFactor.type === item);
// }
</script>